import { useEffect, useRef } from 'react'
import {
  initClarity,
  setClarityPage,
  setSignupStage,
  stopClarity,
  trackClarityEvent,
  trackClarityEventOnce,
} from './microsoftClarity'

const SECTION_EVENTS = [
  ['pricing', 'pricing_page_view', 'pricing_viewed'],
  ['how', 'features_page_view', 'features_viewed'],
  ['faq', 'faq_view', 'faq_viewed'],
  ['contact', 'contact_page_view', 'contact_viewed'],
]

/**
 * Starts Clarity on public pages, stops it on the editor and signing flows,
 * and records marketing page views without duplicating them on rerender.
 */
export function useClarityTracking({
  authLoading,
  view,
  isRecipientView,
  hasSigningLinkId,
  marketingPathname,
  isAuthModalOpen,
  authMode,
  signupStep,
}) {
  const signupViewOpen = useRef(false)

  useEffect(() => {
    if (authLoading) return undefined
    const sensitive = hasSigningLinkId || isRecipientView || view === 'app' || view === 'email-activity'
    if (sensitive) {
      stopClarity()
      return undefined
    }
    initClarity()
    return undefined
  }, [authLoading, view, isRecipientView, hasSigningLinkId])

  useEffect(() => {
    if (authLoading || hasSigningLinkId || isRecipientView || view === 'app' || view === 'email-activity') return undefined
    const path = marketingPathname || '/'
    setClarityPage(path)
    if (path === '/' || path === '/index.html') {
      trackClarityEventOnce('homepage_view')
    }
    if (path === '/pricing') {
      setSignupStage('pricing_viewed')
      trackClarityEventOnce('pricing_page_view')
    }
    if (path === '/how-it-works') {
      setSignupStage('features_viewed')
      trackClarityEventOnce('features_page_view')
    }
    if (path === '/contact') {
      setSignupStage('contact_viewed')
      trackClarityEventOnce('contact_page_view')
    }
    return undefined
  }, [authLoading, view, isRecipientView, hasSigningLinkId, marketingPathname])

  useEffect(() => {
    if (authLoading || hasSigningLinkId || isRecipientView || view !== 'auth') return undefined
    if (typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return
        const match = SECTION_EVENTS.find(([id]) => id === entry.target.id)
        if (!match) return
        setSignupStage(match[2])
        trackClarityEventOnce(match[1])
      })
    }, { threshold: [0.35] })
    SECTION_EVENTS.forEach(([id]) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [authLoading, view, isRecipientView, hasSigningLinkId, marketingPathname])

  useEffect(() => {
    const open = isAuthModalOpen && authMode === 'signup' && signupStep === 1
    if (!open) {
      signupViewOpen.current = false
      return undefined
    }
    if (signupViewOpen.current) return undefined
    signupViewOpen.current = true
    setSignupStage('signup_form')
    trackClarityEvent('signup_page_view')
    return undefined
  }, [isAuthModalOpen, authMode, signupStep])
}
